import { useState, useEffect } from 'react'
import { supabase } from '../supabaseClient'
import { getTeamLabel } from '../utils/teamLabels'
import { getTeamOptions, getSportOptions } from '../utils/fixtureFilters'
import MatchReportModal from '../components/MatchReportModal'
import AIMatchReportModal from '../components/AIMatchReportModal'
import AIMatchAnalysisModal from '../components/AIMatchAnalysisModal'
import TeamSheetModal from '../components/TeamSheetModal'
import FixturesExportModal from '../components/FixturesExportModal'
import { DEFAULT_LOGO, getClubLogo, CLUB_LOGOS } from '../clubLogos'
import { getAllClubs, ALL_COUNTIES, findClub } from '../clubData'
import { generateTeamSheetPdf, loadTeamSheetFonts, loadLogoBytes } from '../utils/generateTeamSheet'
import {
  buildBaseSquadSlug, getBirthYearForGrade, findCollidingSquad, appendLabelToSlug, appendLabelToName, areNamesIndistinguishable,
  findAvailableSlug, notifySquadRelabeled, RETROACTIVE_LABEL, COLLISION_LABEL_PLACEHOLDERS
} from '../utils/squadPublicUrl'
const CLUB_NAME = null // now derived dynamically from profile
const ALL_GAA_CLUBS = getAllClubs().map(({ name, county }) => ({ name, county, logo: getClubLogo(name) }))
const DUBLIN_GAA_CLUBS = ALL_GAA_CLUBS // kept for compatibility with EditFixtureForm

export default function Fixtures({ session, profile, onOpenMatch, onTabChange }) {
  const [fixtures, setFixtures] = useState([])
  const [matchEvents, setMatchEvents] = useState([])
  const [squads, setSquads] = useState([])
  const [showAdd, setShowAdd] = useState(false)
  const [showBulkAdd, setShowBulkAdd] = useState(false)
  const [bulkFixtures, setBulkFixtures] = useState([])
  const [bulkLoading, setBulkLoading] = useState(false)
  const [showPaste, setShowPaste] = useState(false)
  const [pasteText, setPasteText] = useState('')
  const [activeTab, setActiveTab] = useState('upcoming')
  const [editingFixture, setEditingFixture] = useState(null)
  const [clubSearch, setClubSearch] = useState('')
  const [countyFilter, setCountyFilter] = useState(profile?.clubs?.county || '')
  const [showClubPicker, setShowClubPicker] = useState(false)
  const [filterTeam, setFilterTeam] = useState('all')
  const [filterSport, setFilterSport] = useState('all')
  const [filterSquad, setFilterSquad] = useState('all')
  const [filterSeason, setFilterSeason] = useState(new Date().getFullYear())
  const [searchOpp, setSearchOpp] = useState('')
  const [reportFixture, setReportFixture] = useState(null)
  const [aiReportFixture, setAIReportFixture] = useState(null)
  const [aiAnalysisFixture, setAIAnalysisFixture] = useState(null)
  const [pendingCount, setPendingCount] = useState(0)
  const [teamSheetFixture, setTeamSheetFixture] = useState(null)
  const [showExport, setShowExport] = useState(false)
  const [generateCollision, setGenerateCollision] = useState(null)
  const [generateLabel, setGenerateLabel] = useState('')
  const [generateLabelPlaceholder] = useState(() => COLLISION_LABEL_PLACEHOLDERS[Math.floor(Math.random() * COLLISION_LABEL_PLACEHOLDERS.length)])
  const myClubLogo = getClubLogo(profile?.clubs?.club_name || profile?.clubs?.name)

  const defaultTime = () => {
    const d = new Date()
    d.setHours(14, 0, 0, 0)
    return d.toISOString().slice(0, 16)
  }

  const [form, setForm] = useState({
    opponent: '', sport: 'football', squad_id: '', team_number: '1',
    fixture_date: defaultTime(), venue: '', venue_url: '', game_type: 'league'
  })

  useEffect(() => { if (profile) { loadFixtures(); loadSquads(); loadPendingCount() } }, [profile])

  async function loadFixtures() {
    const isAdmin = profile?.role === 'club_admin'
    const isSquadAdmin = profile?.role === 'squad_admin'
    let query = supabase.from('fixtures')
      .select('*, match_report, match_report_published, squads(name, age_group, current_grade, gender, slug, team1_label, team2_label)').eq('club_id', profile.club_id)
      .order('fixture_date', { ascending: true })

    // Coaches only see fixtures for their assigned squads
    if ((!isAdmin) && profile?.squadIds && profile.squadIds.length > 0) {
      query = query.in('squad_id', profile.squadIds)
    } else if ((!isAdmin) && (!profile?.squadIds || profile.squadIds.length === 0)) {
      // No squad assigned — show nothing
      setFixtures([])
      setMatchEvents([])
      return
    }

    const { data } = await query
    setFixtures(data || [])
    if (data) {
      const completedIds = data.filter(f => f.status === 'completed').map(f => f.id)
      if (completedIds.length > 0) {
        const { data: events } = await supabase.from('match_events')
          .select('*, players(full_name)')
          .in('fixture_id', completedIds)
        setMatchEvents(events || [])
      }
    }
  }

  async function loadSquads() {
  const isAdmin = profile?.role === 'club_admin'
  let query = supabase.from('squads').select('*, squad_teams(team_number, label)').eq('club_id', profile.club_id)
    if (!isAdmin && profile?.squadIds && profile.squadIds.length > 0) {
      query = query.in('id', profile.squadIds)
    }
    const { data } = await query
    setSquads(data || [])
  }

  // Self-serve fix for a squad that has no public URL yet (e.g. it was
  // created via a path that didn't generate one) — mirrors the same
  // creation-time logic used at signup/squad-creation.
  async function generatePublicUrlForSquad(squad, label) {
    const clubSlug = profile.clubs?.slug
    const seasonYear = new Date().getFullYear()
    const gradeText = squad.current_grade || squad.age_group
    const baseSlug = buildBaseSquadSlug({ clubSlug, gradeText, gender: squad.gender, sport: squad.sport, seasonYear })
    const birthYear = getBirthYearForGrade(gradeText, seasonYear)
    let name = squad.name
    let finalSlug = baseSlug
    let publicUrlLabelValue = null
    if (label) {
      publicUrlLabelValue = label.trim()
      name = appendLabelToName(name, publicUrlLabelValue)
      finalSlug = appendLabelToSlug(baseSlug, publicUrlLabelValue)
    }
    finalSlug = await findAvailableSlug(supabase, finalSlug)
    await supabase.from('squads').update({
      name, birth_year: birthYear, public_url_label: publicUrlLabelValue, slug: finalSlug
    }).eq('id', squad.id)
  }

  async function handleGeneratePublicUrl(squad) {
    const candidate = { current_grade: squad.current_grade, age_group: squad.age_group, gender: squad.gender, sport: squad.sport }
    const match = findCollidingSquad(squads, candidate, { excludeId: squad.id })
    if (match) {
      setGenerateCollision({ squad, match })
      setGenerateLabel('')
      return
    }
    await generatePublicUrlForSquad(squad, null)
    await loadSquads()
  }

  async function confirmGenerateWithLabel() {
    if (!generateCollision || !generateLabel.trim()) return
    const { squad, match } = generateCollision
    await generatePublicUrlForSquad(squad, generateLabel.trim())

    if (!match.public_url_label && areNamesIndistinguishable(squad.name, match.name)) {
      const clubSlug = profile.clubs?.slug
      const seasonYear = new Date().getFullYear()
      const existingBaseSlug = buildBaseSquadSlug({
        clubSlug, gradeText: match.current_grade || match.age_group, gender: match.gender, sport: match.sport, seasonYear
      })
      const existingName = appendLabelToName(match.name, RETROACTIVE_LABEL)
      const existingSlug = await findAvailableSlug(supabase, appendLabelToSlug(existingBaseSlug, RETROACTIVE_LABEL))
      await supabase.from('squads').update({
        name: existingName, slug: existingSlug, public_url_label: RETROACTIVE_LABEL
      }).eq('id', match.id)
      await notifySquadRelabeled(supabase, {
        squad: match, newName: existingName, newSlug: existingSlug, clubName: profile.clubs?.club_name || profile.clubs?.name || ''
      })
    }

    setGenerateCollision(null)
    await loadSquads()
  }

  async function loadPendingCount() {
    const isAdmin = profile?.role === 'club_admin'
    const isSquadAdmin = profile?.role === 'squad_admin'
    if (!isAdmin && !isSquadAdmin) { setPendingCount(0); return }
    const { data } = await supabase.from('profiles')
      .select('id, approved, squad_members!squad_members_profile_id_fkey(squad_id)')
      .eq('club_id', profile.club_id)
      .eq('approved', false)
    if (!data) { setPendingCount(0); return }
    if (isAdmin) {
      setPendingCount(data.length)
    } else {
      const mySquadIds = profile?.squadIds || []
      const pending = data.filter(c => (c.squad_members || []).some(sm => mySquadIds.includes(sm.squad_id)))
      setPendingCount(pending.length)
    }
  }

  async function addFixture() {
    if (!form.opponent || !form.squad_id) { alert('Please fill in opponent and select a squad.'); return }
    const fixtureYear = form.fixture_date ? new Date(form.fixture_date).getFullYear() : new Date().getFullYear()
    const { error } = await supabase.from('fixtures').insert({
      ...form, team_number: parseInt(form.team_number),
      club_id: profile.club_id, status: 'upcoming',
      season: fixtureYear,
      home_goals: 0, home_points: 0, away_goals: 0, away_points: 0
    })
    if (error) { alert('Error: ' + error.message); return }
    setForm({ opponent: '', sport: 'football', squad_id: '', team_number: '1', fixture_date: defaultTime(), venue: '', venue_url: '', game_type: 'league' })
    setShowAdd(false)
    setClubSearch('')
    loadFixtures()
  }
  function parsePasteText() {
    const lines = pasteText.split('\n').map(l => l.trim()).filter(l => l.length > 0)
    const rows = lines.map(line => {
      const parts = line.split(/[\t,]/).map(p => p.trim())
      const opponent = parts[0] || ''
      const dateStr = parts[1] || ''
      const timeStr = parts[2] || '14:00'
      const sport = (parts[3] || 'football').toLowerCase().includes('hurl') ? 'hurling' : 'football'
      const gameType = ['league','championship','cup','friendly'].find(t => (parts[4] || '').toLowerCase().includes(t)) || 'league'
      const teamNumber = parts[5] === '2' ? '2' : '1'
      const venue = parts[6] || ''

      // Parse date DD/MM/YYYY or MM/DD/YYYY
      let fixture_date = defaultTime()
      if (dateStr) {
        const dateParts = dateStr.split('/').map(Number)
        if (dateParts.length === 3) {
          const [day, month, year] = dateParts
          const timeParts = timeStr.split(':').map(Number)
          const d = new Date(Date.UTC(year, month - 1, day, timeParts[0] || 14, timeParts[1] || 0))
          if (!isNaN(d)) fixture_date = d.toISOString().slice(0, 16)
        }
      }
      return { opponent, sport, squad_id: squads[0]?.id || '', team_number: teamNumber, fixture_date, venue, venue_url: '', game_type: gameType }
    }).filter(r => r.opponent)

    if (rows.length === 0) { alert('No valid fixtures found. Check format.'); return }
    setBulkFixtures(rows)
    setShowPaste(false)
    setShowBulkAdd(true)
    setPasteText('')
  }

  function defaultBulkRow() {
    return { opponent: '', sport: 'football', squad_id: squads[0]?.id || '', team_number: '1', fixture_date: defaultTime(), venue: '', venue_url: '', game_type: 'league' }
  }

  async function bulkAddFixtures() {
    const valid = bulkFixtures.filter(f => f.opponent && f.squad_id)
    if (valid.length === 0) { alert('Please fill in at least one fixture with opponent and squad.'); return }
    setBulkLoading(true)
    const toInsert = valid.map(f => ({
      ...f, team_number: parseInt(f.team_number),
      club_id: profile.club_id, status: 'upcoming',
      season: f.fixture_date ? new Date(f.fixture_date).getFullYear() : new Date().getFullYear(),
      home_goals: 0, home_points: 0, away_goals: 0, away_points: 0
    }))
    const { error } = await supabase.from('fixtures').insert(toInsert)
    if (error) { alert('Error: ' + error.message); setBulkLoading(false); return }
    setBulkFixtures([])
    setShowBulkAdd(false)
    setBulkLoading(false)
    loadFixtures()
  }

  async function saveEditFixture() {
    if (!editingFixture) return
    const { error } = await supabase.from('fixtures').update({
      opponent: editingFixture.opponent, sport: editingFixture.sport,
      squad_id: editingFixture.squad_id, team_number: parseInt(editingFixture.team_number),
      fixture_date: editingFixture.fixture_date, venue: editingFixture.venue,
      venue_url: editingFixture.venue_url || '',
      game_type: editingFixture.game_type
    }).eq('id', editingFixture.id)
    if (error) { alert('Error: ' + error.message); return }
    setEditingFixture(null)
    loadFixtures()
  }

  async function deleteFixture(id) {
    if (!window.confirm('Delete this fixture? This cannot be undone.')) return
    try {
      await supabase.from('match_events').delete().eq('fixture_id', id)
      await supabase.from('lineups').delete().eq('fixture_id', id)
      const { error } = await supabase.from('fixtures').delete().eq('id', id)
      if (error) { alert('Error deleting fixture: ' + error.message); return }
      setFixtures(prev => prev.filter(f => f.id !== id))
    } catch (e) {
      alert('Error: ' + e.message)
    }
  }


  function totalPts(goals, points) { return (goals * 3) + points }

  function getResult(f) {
    const home = totalPts(f.home_goals, f.home_points)
    const away = totalPts(f.away_goals, f.away_points)
    const diff = Math.abs(home - away)
    const clubName = profile?.clubs?.club_name || profile?.clubs?.name || 'Us'
    if (home > away) return { label: 'W', color: '#3B6D11', bg: '#EAF3DE', text: `${clubName} won by ${diff} pt${diff !== 1 ? 's' : ''}` }
    if (home < away) return { label: 'L', color: '#A32D2D', bg: '#FCEBEB', text: `${clubName} lost by ${diff} pt${diff !== 1 ? 's' : ''}` }
    return { label: 'D', color: '#854F0B', bg: '#FAEEDA', text: 'Draw' }
  }

  function formatScore(f) {
    const hTotal = totalPts(f.home_goals, f.home_points)
    const aTotal = totalPts(f.away_goals, f.away_points)
    return `${f.home_goals}-${String(f.home_points).padStart(2, '0')} (${hTotal}) v ${f.away_goals}-${String(f.away_points).padStart(2, '00')} (${aTotal})`
  }

function formatDate(dateStr) {
  if (!dateStr) return { month: '—', day: '—', time: '', full: '' }
  const d = new Date(dateStr)
  return {
    month: d.toLocaleString('en-IE', { month: 'short', timeZone: 'UTC' }),
    day: d.getUTCDate(),
    time: d.toLocaleString('en-IE', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }),
    full: d.toLocaleString('en-IE', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
  }
}

  function gameTypeBadge(type) {
    const map = {
      league: { bg: '#E6F1FB', color: '#185FA5' },
      championship: { bg: '#FAEEDA', color: '#854F0B' },
      cup: { bg: '#EAF3DE', color: '#3B6D11' },
      friendly: { bg: '#F1EFE8', color: '#5F5E5A' }
    }
    return map[type] || map.league
  }

  function gameTypeLabel(type) {
    return { league: 'League', championship: 'Championship', cup: 'Cup', friendly: 'Friendly' }[type] || type
  }

  const filteredClubs = ALL_GAA_CLUBS.filter(c => {
    const matchesSearch = c.name.toLowerCase().includes(clubSearch.toLowerCase())
    const matchesCounty = !countyFilter || c.county === countyFilter
    return matchesSearch && matchesCounty
  })

  const availableSeasons = [...new Set(fixtures.map(f => f.season).filter(Boolean))].sort((a, b) => b - a)
  let displayFixtures = [...fixtures]
  if (filterSeason) displayFixtures = displayFixtures.filter(f => f.season === filterSeason)
  if (filterTeam !== 'all') displayFixtures = displayFixtures.filter(f => String(f.team_number) === filterTeam)
  if (filterSport !== 'all') displayFixtures = displayFixtures.filter(f => f.sport === filterSport)
  if (filterSquad !== 'all') displayFixtures = displayFixtures.filter(f => f.squad_id === filterSquad)
  if (searchOpp) displayFixtures = displayFixtures.filter(f => f.opponent.toLowerCase().includes(searchOpp.toLowerCase()))

  const upcoming = displayFixtures
    .filter(f => f.status !== 'completed')
    .sort((a, b) => {
      if (a.status === 'live' && b.status !== 'live') return -1
      if (b.status === 'live' && a.status !== 'live') return 1
      return 0
    })

  const completed = displayFixtures.filter(f => f.status === 'completed').reverse()

  return (
    <div>
      {/* Header */}
    <div style={header}>
        <div style={{ flex: 1 }}>
          <h2 style={{ ...h2, marginBottom: 4 }}>Fixtures</h2>
        </div>
        {(profile?.role === 'club_admin' || profile?.role === 'squad_admin') && (
          <div style={{ display: 'flex', gap: '6px' }}>
            <button style={addBtn} onClick={() => { setShowAdd(!showAdd); setShowBulkAdd(false); setShowPaste(false) }}>
              {showAdd ? 'Cancel' : '+ Add Fixture'}
            </button>
            <button style={{ ...addBtn, background: '#E6F1FB', color: '#185FA5', border: '1px solid #185FA5' }}
              onClick={() => { setShowBulkAdd(!showBulkAdd); setShowAdd(false); setShowPaste(false); setBulkFixtures([defaultBulkRow(), defaultBulkRow(), defaultBulkRow()]) }}>
              {showBulkAdd ? 'Cancel' : '+ Add Multiple'}
            </button>
            <button style={{ ...addBtn, background: '#EAF3DE', color: '#3B6D11', border: '1px solid #3B6D11' }}
              onClick={() => { setShowPaste(!showPaste); setShowAdd(false); setShowBulkAdd(false) }}>
              {showPaste ? 'Cancel' : '📋 Paste Fixtures'}
            </button>
          </div>
        )}
      </div>
      {squads.length > 0 && (() => {

      if (filterSquad === 'all') {
        const clubSlug = profile?.clubs?.slug
        if (!clubSlug) return null
        return (
          <div style={{ padding: '8px 16px 0' }}>
            <span
              onClick={() => window.open('https://gaamatchtracker.ie/public/club/' + clubSlug, '_blank')}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px', background: '#E6F1FB', color: '#185FA5', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
            >
              🔗 Share Club Live Score Page
            </span>
          </div>
        )
      }
      const selectedSquad = squads.find(s => s.id === filterSquad)
      if (!selectedSquad) return null
        if (selectedSquad.slug) {
          return (
            <div style={{ padding: '8px 16px 0' }}>
              <span
                onClick={() => window.open('https://gaamatchtracker.ie/public/' + selectedSquad.slug, '_blank')}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px', background: '#E6F1FB', color: '#185FA5', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                🔗 Share Live Score Page
              </span>
            </div>
          )
        }
        if (profile?.role === 'club_admin' || profile?.role === 'squad_admin') {
          return (
            <div style={{ padding: '8px 16px 0' }}>
              <span
                onClick={() => handleGeneratePublicUrl(selectedSquad)}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 12px', background: '#FFF6E5', color: '#7A5B10', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                ⚡ Generate Public URL
              </span>
            </div>
          )
        }
        return null
      })()}
      {generateCollision && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: '20px' }}>
          <div style={{ background: 'white', borderRadius: '16px', padding: '24px', width: '100%', maxWidth: '340px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>What's different about this squad?</h3>
            <p style={{ fontSize: '13px', color: '#555', lineHeight: '1.6', marginBottom: '14px' }}>
              Your club already has a squad with a public page at this grade/gender/sport — <strong>{generateCollision.match.name}</strong>. What should we call this one?
            </p>
            <input style={{ display: 'block', width: '100%', padding: '12px 14px', marginBottom: '16px', border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', outline: 'none' }}
              type="text" placeholder={generateLabelPlaceholder} value={generateLabel} onChange={e => setGenerateLabel(e.target.value)} autoFocus />
            <div style={{ display: 'flex', gap: '8px' }}>
              <button style={{ flex: 1, padding: '12px', border: '0.5px solid #ccc', borderRadius: '8px', background: 'white', cursor: 'pointer', fontSize: '14px' }}
                onClick={() => setGenerateCollision(null)}>Cancel</button>
              <button style={{ flex: 1, padding: '12px', border: 'none', borderRadius: '8px', background: '#185FA5', color: 'white', cursor: 'pointer', fontSize: '14px', fontWeight: 500 }}
                disabled={!generateLabel.trim()} onClick={confirmGenerateWithLabel}>Confirm</button>
            </div>
          </div>
        </div>
      )}
      {/* Pending coach requests banner */}
      {pendingCount > 0 && (profile?.role === 'club_admin' || profile?.role === 'squad_admin') && (
        <div onClick={() => { if (typeof onTabChange === 'function') onTabChange('admin') }}
          style={{ margin: '0 16px 12px', padding: '12px 14px', background: '#FAEEDA', border: '0.5px solid #EF9F27', borderRadius: '10px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}>
          <span style={{ fontSize: '13px', color: '#854F0B' }}>
            ⏳ {pendingCount} coach{pendingCount !== 1 ? 'es' : ''} waiting for approval
          </span>
          <span style={{ fontSize: '12px', color: '#854F0B', fontWeight: '500' }}>Review →</span>
        </div>
      )}

      {/* Paste fixtures form */}
      {showPaste && (
        <div style={{ margin: '12px 16px', padding: '14px', background: '#f8f8f8', borderRadius: '12px' }}>
          <p style={{ fontSize: '13px', color: '#888', marginBottom: '4px' }}>Paste fixtures — one per line</p>
          <p style={{ fontSize: '11px', color: '#aaa', marginBottom: '8px' }}>
            Format: Opponent, DD/MM/YYYY, HH:MM, Sport, Type, Team, Venue{'\n'}
            e.g. "St Judes, 15/06/2026, 14:00, football, league, 1, Tymon Park"{'\n'}
            Only opponent is required. Works with tab-separated spreadsheet paste too.
          </p>
          <textarea
            style={{ display: 'block', width: '100%', padding: '10px', border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '13px', fontFamily: 'monospace', height: '160px', resize: 'vertical', boxSizing: 'border-box', marginBottom: '10px' }}
            placeholder={'St Judes, 15/06/2026, 14:00, football, league, 1, Tymon Park\nNa Fianna, 22/06/2026, 11:00\nCuala, 29/06/2026, 14:30, football, championship'}
            value={pasteText}
            onChange={e => setPasteText(e.target.value)}
          />
          <p style={{ fontSize: '11px', color: '#888', marginBottom: '8px' }}>
            {pasteText.split('\n').filter(l => l.trim()).length} rows detected
          </p>
          <button onClick={parsePasteText}
            style={{ display: 'block', width: '100%', padding: '12px', background: '#3B6D11', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }}>
            Preview Fixtures →
          </button>
        </div>
      )}

{/* Bulk add fixtures form */}
      {showBulkAdd && (
        <div style={{ margin: '12px 16px', padding: '14px', background: '#f8f8f8', borderRadius: '12px' }}>
          <p style={{ fontSize: '13px', color: '#888', marginBottom: '10px' }}>Bulk add fixtures — fill in the rows below</p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                  {['Opponent *', 'Squad *', 'Date & Time', 'Sport', 'Type', 'Team', 'Venue', ''].map(h => (
                    <th key={h} style={{ padding: '6px 4px', textAlign: 'left', color: '#888', fontWeight: '500', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {bulkFixtures.map((f, i) => (
                  <tr key={i} style={{ borderBottom: '0.5px solid #f0f0f0' }}>
                    <td style={{ padding: '4px' }}>
                      <input style={{ width: '120px', padding: '5px', border: `1px solid ${f.opponent && !findClub(f.opponent) ? '#854F0B' : '#e0e0e0'}`, borderRadius: '6px', fontSize: '12px' }}
                        placeholder="Opponent"
                        value={f.opponent}
                        onChange={e => { const n=[...bulkFixtures]; n[i]={...n[i],opponent:e.target.value}; setBulkFixtures(n) }} />
                      {f.opponent && !findClub(f.opponent) && (
                        <div style={{ fontSize: '10px', color: '#854F0B', marginTop: '2px' }}>⚠️ Not recognised</div>
                      )}
                    </td>
                    <td style={{ padding: '4px' }}>
                      <select style={{ padding: '5px', border: '1px solid #e0e0e0', borderRadius: '6px', fontSize: '12px' }}
                        value={f.squad_id}
                        onChange={e => { const n=[...bulkFixtures]; n[i]={...n[i],squad_id:e.target.value}; setBulkFixtures(n) }}>
                        <option value="">Select</option>
                        {squads.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </td>
                    <td style={{ padding: '4px' }}>
                      <input type="datetime-local" style={{ padding: '5px', border: '1px solid #e0e0e0', borderRadius: '6px', fontSize: '12px' }}
                        value={f.fixture_date}
                        onChange={e => { const n=[...bulkFixtures]; n[i]={...n[i],fixture_date:e.target.value}; setBulkFixtures(n) }} />
                    </td>
                    <td style={{ padding: '4px' }}>
                      <select style={{ padding: '5px', border: '1px solid #e0e0e0', borderRadius: '6px', fontSize: '12px' }}
                        value={f.sport}
                        onChange={e => { const n=[...bulkFixtures]; n[i]={...n[i],sport:e.target.value}; setBulkFixtures(n) }}>
                        <option value="football">Football</option>
                        <option value="hurling">Hurling</option>
                      </select>
                    </td>
                    <td style={{ padding: '4px' }}>
                      <select style={{ padding: '5px', border: '1px solid #e0e0e0', borderRadius: '6px', fontSize: '12px' }}
                        value={f.game_type}
                        onChange={e => { const n=[...bulkFixtures]; n[i]={...n[i],game_type:e.target.value}; setBulkFixtures(n) }}>
                        <option value="league">League</option>
                        <option value="championship">Championship</option>
                        <option value="cup">Cup</option>
                        <option value="friendly">Friendly</option>
                      </select>
                    </td>
                    <td style={{ padding: '4px' }}>
                      <select style={{ padding: '5px', border: '1px solid #e0e0e0', borderRadius: '6px', fontSize: '12px' }}
                        value={f.team_number}
                        onChange={e => { const n=[...bulkFixtures]; n[i]={...n[i],team_number:e.target.value}; setBulkFixtures(n) }}>
                        <option value="1">T1</option>
                        <option value="2">T2</option>
                      </select>
                    </td>
                    <td style={{ padding: '4px' }}>
                      <input style={{ width: '100px', padding: '5px', border: '1px solid #e0e0e0', borderRadius: '6px', fontSize: '12px' }}
                        placeholder="Venue"
                        value={f.venue}
                        onChange={e => { const n=[...bulkFixtures]; n[i]={...n[i],venue:e.target.value}; setBulkFixtures(n) }} />
                    </td>
                    <td style={{ padding: '4px' }}>
                      <button onClick={() => setBulkFixtures(bulkFixtures.filter((_,j) => j !== i))}
                        style={{ padding: '4px 8px', border: '0.5px solid #A32D2D', borderRadius: '6px', background: '#FCEBEB', color: '#A32D2D', cursor: 'pointer', fontSize: '12px' }}>✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
            <button onClick={() => setBulkFixtures([...bulkFixtures, defaultBulkRow()])}
              style={{ padding: '8px 14px', border: '1px dashed #ccc', borderRadius: '8px', background: 'none', fontSize: '13px', cursor: 'pointer', color: '#888' }}>
              + Add Row
            </button>
            <button onClick={bulkAddFixtures} disabled={bulkLoading}
              style={{ flex: 1, padding: '10px', background: '#185FA5', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }}>
              {bulkLoading ? 'Saving...' : 'Save ' + bulkFixtures.filter(f => f.opponent && f.squad_id).length + ' Fixtures'}
            </button>
          </div>
        </div>
      )}

      {/* Add fixture form */}
      {showAdd && (
        <div style={formBox}>
          <p style={formTitle}>New fixture</p>
        <div style={{ marginBottom: '10px', position: 'relative' }}>
            <div style={{ display: 'flex', gap: '6px', marginBottom: '6px' }}>
              <select style={{ flex: 1, padding: '8px', border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '13px', background: 'white' }}
                value={countyFilter} onChange={e => { setCountyFilter(e.target.value); setShowClubPicker(true) }}>
                <option value="">All Counties</option>
                {ALL_COUNTIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '8px 12px', background: 'white' }}>
              {form.opponent && (
                <img src={getClubLogo(form.opponent)} alt="" style={{ width: '28px', height: '28px', borderRadius: '50%', objectFit: 'contain' }}
                  onError={e => { e.target.src = DEFAULT_LOGO }} />
              )}
              <input style={{ border: 'none', outline: 'none', fontSize: '14px', flex: 1, background: 'none' }}
                placeholder="Search or type opponent *"
                value={form.opponent}
                onChange={e => { setForm({ ...form, opponent: e.target.value }); setClubSearch(e.target.value); setShowClubPicker(true) }}
                onFocus={() => setShowClubPicker(true)}
              />
            </div>
            {showClubPicker && (
              <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e0e0e0', borderRadius: '8px', maxHeight: '200px', overflowY: 'auto', zIndex: 50, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                {filteredClubs.slice(0, 25).map(c => (
                  <div key={c.name + c.county} onClick={() => { setForm({ ...form, opponent: c.name }); setShowClubPicker(false); setClubSearch(c.name); setCountyFilter(c.county) }}
                    style={{ padding: '8px 12px', borderBottom: '0.5px solid #f0f0f0', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <img src={c.logo} alt="" style={{ width: '28px', height: '28px', borderRadius: '50%', objectFit: 'contain' }}
                      onError={e => { e.target.src = DEFAULT_LOGO }} />
                    <div style={{ flex: 1 }}>
                      <span>{c.name}</span>
                      <span style={{ fontSize: '11px', color: '#aaa', marginLeft: '6px' }}>{c.county}</span>
                    </div>
                  </div>
                ))}
                {form.opponent && !ALL_GAA_CLUBS.find(c => c.name === form.opponent) && (
                  <div onClick={() => setShowClubPicker(false)}
                    style={{ padding: '10px 12px', cursor: 'pointer', fontSize: '13px', color: '#185FA5', fontStyle: 'italic' }}>
                    Use "{form.opponent}" as entered
                  </div>
                )}
              </div>
            )}
          </div>
          <select style={input} value={form.squad_id} onChange={e => setForm({ ...form, squad_id: e.target.value })}>
            <option value="">Select squad *</option>
            {squads.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <select style={input} value={form.team_number} onChange={e => setForm({ ...form, team_number: e.target.value })}>
            {getTeamOptions(squads, form.squad_id || 'all', fixtures).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
          <select style={input} value={form.sport} onChange={e => setForm({ ...form, sport: e.target.value })}>
            <option value="football">Gaelic Football</option>
            <option value="hurling">Hurling</option>
          </select>
          <select style={input} value={form.game_type} onChange={e => setForm({ ...form, game_type: e.target.value })}>
            <option value="league">League</option>
            <option value="championship">Championship</option>
            <option value="cup">Cup</option>
            <option value="friendly">Friendly</option>
          </select>
          <input style={input} type="datetime-local" value={form.fixture_date}
            onChange={e => setForm({ ...form, fixture_date: e.target.value })} />
          <input style={input} placeholder="Venue (optional)" value={form.venue}
            onChange={e => setForm({ ...form, venue: e.target.value })} />
            <input style={input} placeholder="Venue map link (optional)" value={form.venue_url || ''}
            onChange={e => setForm({ ...form, venue_url: e.target.value })} />
          <button style={saveBtn} onClick={addFixture}>Save Fixture</button>
        </div>
      )}

{/* Filters */}
<div style={{ padding: '8px 16px', borderBottom: '0.5px solid #e0e0e0', display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
  {availableSeasons.length > 1 && (
    <select value={filterSeason} onChange={e => setFilterSeason(Number(e.target.value))}
      style={{ padding: '6px 8px', border: '0.5px solid #e0e0e0', borderRadius: '20px', fontSize: '12px', background: '#f8f8f8' }}>
      {availableSeasons.map(s => <option key={s} value={s}>{s}</option>)}
    </select>
  )}
  <input placeholder="🔍 Search opponent" value={searchOpp}
    onChange={e => setSearchOpp(e.target.value)}
    style={{ flex: 1, minWidth: '120px', padding: '6px 10px', border: '0.5px solid #e0e0e0', borderRadius: '20px', fontSize: '13px', background: '#f8f8f8' }} />
  {(() => {
    const teamOptions = getTeamOptions(squads, filterSquad, fixtures)
    if (teamOptions.length < 2 && filterTeam === 'all') return null
    return (
      <select value={filterTeam} onChange={e => setFilterTeam(e.target.value)}
        style={{ padding: '6px 8px', border: '0.5px solid #e0e0e0', borderRadius: '20px', fontSize: '12px', background: '#f8f8f8' }}>
        <option value="all">All Teams</option>
        {teamOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    )
  })()}
  {(() => {
    const sportOptions = getSportOptions(fixtures)
    if (sportOptions.length < 2 && filterSport === 'all') return null
    return (
      <select value={filterSport} onChange={e => setFilterSport(e.target.value)}
        style={{ padding: '6px 8px', border: '0.5px solid #e0e0e0', borderRadius: '20px', fontSize: '12px', background: '#f8f8f8' }}>
        <option value="all">All Sports</option>
        {sportOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    )
  })()}
  {profile?.role === 'club_admin' && squads.length > 1 && (
    <select value={filterSquad} onChange={e => setFilterSquad(e.target.value)}
      style={{ padding: '6px 8px', border: '0.5px solid #e0e0e0', borderRadius: '20px', fontSize: '12px', background: '#f8f8f8' }}>
      <option value="all">All Squads</option>
      {squads.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
    </select>
  )}
</div>

  {/* Export button — admins only */}
      {(profile?.role === 'club_admin' || profile?.role === 'squad_admin') && fixtures.length > 0 && (
        <div style={{ padding: '8px 16px', display: 'flex', justifyContent: 'flex-end' }}>
          <button onClick={() => setShowExport(true)}
            style={{ fontSize: '12px', padding: '6px 12px', borderRadius: '8px', border: '0.5px solid #533AB7', background: '#EEEDFE', color: '#533AB7', cursor: 'pointer', fontWeight: '500' }}>
            ⬇ Export Fixtures
          </button>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: '0.5px solid #e0e0e0' }}>
        {['upcoming', 'results'].map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            style={{ flex: 1, padding: '12px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: '500', color: activeTab === tab ? '#185FA5' : '#888', borderBottom: activeTab === tab ? '2px solid #185FA5' : '2px solid transparent' }}>
            {tab === 'upcoming' ? `Upcoming (${upcoming.length})` : `Results (${completed.length})`}
          </button>
        ))}
      </div>

      {/* Upcoming */}
      {activeTab === 'upcoming' && (
        <>
          {upcoming.length === 0 && (
            <div style={{ padding: '40px', textAlign: 'center', color: '#888', fontSize: '14px' }}>
              No upcoming fixtures. Tap + Add to create one.
            </div>
          )}
          {upcoming.map(f => {
            const date = formatDate(f.fixture_date)
            const badge = gameTypeBadge(f.game_type)
            const oppLogo = getClubLogo(f.opponent)
            const activePhases = ['playing', 'ht', 'et1', 'et_ht', 'et2']
            const isLive = f.status === 'live' && (f.clock_running || activePhases.includes(f.match_phase))
            const isLineupConfirmed = f.status === 'live' && !isLive
            const phaseLabel =
              f.match_phase === 'ht' ? '🔴 LIVE · HT' :
              f.match_phase === 'et_ht' ? '🔴 LIVE · ET HT' :
              '🔴 LIVE'
            return (
              <div key={f.id}>
                {editingFixture?.id === f.id ? (
                  <EditFixtureForm fixture={editingFixture} squads={squads} clubs={DUBLIN_GAA_CLUBS}
                    onChange={setEditingFixture} onSave={saveEditFixture} onCancel={() => setEditingFixture(null)} />
                ) : (
                  <div style={fixtureRow}>
                    <div onClick={() => onOpenMatch(f)} style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, cursor: 'pointer' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0 }}>
                        <img src={myClubLogo || DEFAULT_LOGO} alt="" style={{ width: '28px', height: '28px', borderRadius: '50%', objectFit: 'contain' }} onError={e => { e.target.src = DEFAULT_LOGO }} />
                        <span style={{ fontSize: '10px', color: '#888' }}>v</span>
                        <img src={oppLogo} alt={f.opponent} style={{ width: '28px', height: '28px', borderRadius: '50%', objectFit: 'contain' }} onError={e => { e.target.src = DEFAULT_LOGO }} />
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: '14px', fontWeight: '500' }}>vs {f.opponent}</p>
                        <p style={{ fontSize: '12px', color: '#888', marginTop: '1px' }}>
                          {f.squads?.name} · Team {f.team_number || 1} · {f.sport === 'football' ? 'Football' : 'Hurling'}
                        </p>
                        {f.fixture_date && (
                          <p style={{ fontSize: '11px', color: '#aaa', marginTop: '1px' }}>
                            📅 {date.full} · {date.time}{f.venue ? ` · 📍 ${f.venue}` : ''}
                          </p>
                        )}
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '5px' }}>
                          <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '20px', background: isLive ? '#FF3B30' : isLineupConfirmed ? '#3B6D11' : '#FAEEDA', color: isLive ? 'white' : isLineupConfirmed ? 'white' : '#854F0B', fontWeight: '500', whiteSpace: 'nowrap' }}>
                            {isLive ? phaseLabel : isLineupConfirmed ? '✅ Lineup Confirmed' : 'Upcoming'}
                          </span>
                          <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '20px', background: badge.bg, color: badge.color, fontWeight: '500', whiteSpace: 'nowrap' }}>{gameTypeLabel(f.game_type)}</span>
                        </div>
                    <PreviousResults fixture={f} allFixtures={fixtures} onOpen={onOpenMatch} />
                      </div>
                    </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginLeft: '6px' }}>
      {(profile?.role === 'club_admin' || profile?.role === 'squad_admin') && (
        <>
          <button onClick={() => onOpenMatch(f)}
            style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '6px', border: '0.5px solid #185FA5', background: '#E6F1FB', color: '#185FA5', cursor: 'pointer' }}>
            {isLineupConfirmed ? '✏️ Edit Lineup' : '📋 Pick Lineup'}
          </button>
          <button onClick={() => setEditingFixture({ ...f })} style={editBtn}>Edit Fixture</button>
          <button onClick={() => deleteFixture(f.id)} style={deleteBtn}>Del</button>
          {f.status === 'live' && (
            <button onClick={() => setTeamSheetFixture(f)}
              style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '6px', border: '0.5px solid #533AB7', background: '#EEEDFE', color: '#533AB7', cursor: 'pointer' }}>
              📄 Sheet
            </button>
          )}
        </>
      )}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </>
      )}

      {/* Results */}
      {activeTab === 'results' && (
        <>
          {completed.length === 0 && (
            <div style={{ padding: '40px', textAlign: 'center', color: '#888', fontSize: '14px' }}>No results yet.</div>
          )}
          {completed.map(f => {
            const date = formatDate(f.fixture_date)
            const result = getResult(f)
            const badge = gameTypeBadge(f.game_type)
            const oppLogo = getClubLogo(f.opponent)
            const fEvents = matchEvents.filter(e => e.fixture_id === f.id)
            const scorerMap = {}
            fEvents.filter(e => ['goal','point','twopoint'].includes(e.event_type)).forEach(e => {
              const name = e.players?.full_name || 'Unknown'
              if (!scorerMap[name]) scorerMap[name] = { goals: 0, points: 0 }
              if (e.event_type === 'goal') scorerMap[name].goals++
              if (e.event_type === 'point') scorerMap[name].points++
              if (e.event_type === 'twopoint') scorerMap[name].points += 2
            })
            const topScorers = Object.entries(scorerMap)
              .sort((a, b) => (b[1].goals * 3 + b[1].points) - (a[1].goals * 3 + a[1].points))
              .slice(0, 3)
            const yellowCards = fEvents.filter(e => e.event_type === 'yellow_card').map(e => e.players?.full_name).filter(Boolean)
            const redCards = fEvents.filter(e => e.event_type === 'red_card').map(e => e.players?.full_name).filter(Boolean)
            const blackCards = fEvents.filter(e => e.event_type === 'black_card').map(e => e.players?.full_name).filter(Boolean)
            return (
              <div key={f.id}>
                {editingFixture?.id === f.id ? (
                  <EditFixtureForm fixture={editingFixture} squads={squads} clubs={DUBLIN_GAA_CLUBS}
                    onChange={setEditingFixture} onSave={saveEditFixture} onCancel={() => setEditingFixture(null)} />
                ) : (
                  <div style={{ padding: '12px 16px', borderBottom: '0.5px solid #f0f0f0' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px', flexShrink: 0, marginTop: '2px' }}>
                        <img src={myClubLogo || DEFAULT_LOGO} alt="" style={{ width: '28px', height: '28px', borderRadius: '50%', objectFit: 'contain' }} onError={e => { e.target.src = DEFAULT_LOGO }} />
                        <span style={{ fontSize: '10px', color: '#888' }}>v</span>
                        <img src={oppLogo} alt={f.opponent} style={{ width: '28px', height: '28px', borderRadius: '50%', objectFit: 'contain' }} onError={e => { e.target.src = DEFAULT_LOGO }} />
                      </div>
                      <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => onOpenMatch(f)}>
                        <p style={{ fontSize: '14px', fontWeight: '500' }}>vs {f.opponent}</p>
                        <p style={{ fontSize: '11px', color: '#888', marginTop: '1px' }}>
                          {f.squads?.name} · Team {f.team_number || 1} · {f.sport === 'football' ? 'Football' : 'Hurling'}
                        </p>
                        {f.fixture_date && (
                          <p style={{ fontSize: '11px', color: '#aaa', marginTop: '1px' }}>📅 {date.full}{f.venue ? ` · 📍 ${f.venue}` : ''}</p>
                        )}
                        <p style={{ fontSize: '14px', fontWeight: '600', marginTop: '4px', color: '#333', letterSpacing: '-0.3px' }}>
                          {formatScore(f)}
                        </p>
                        <p style={{ fontSize: '12px', color: result.color, marginTop: '1px', fontWeight: '500' }}>{result.text}</p>
                        <PreviousResults fixture={f} allFixtures={fixtures} />
                        {topScorers.length > 0 && (
                          <div style={{ marginTop: '6px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {topScorers.map(([name, s]) => (
                              <span key={name} style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '20px', background: '#E6F1FB', color: '#185FA5' }}>
                                ⚽ {name.split(' ').slice(-1)[0]} {s.goals > 0 ? `${s.goals}-${String(s.points).padStart(2,'0')}` : `0-${String(s.points).padStart(2,'0')}`}
                              </span>
                            ))}
                          </div>
                        )}
                        {(yellowCards.length > 0 || blackCards.length > 0 || redCards.length > 0) && (
                          <div style={{ marginTop: '4px', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                            {yellowCards.map(name => (
                              <span key={name} style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '20px', background: '#FAEEDA', color: '#854F0B' }}>
                                🟨 {name.split(' ').slice(-1)[0]}
                              </span>
                            ))}
                            {blackCards.map(name => (
                              <span key={name} style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '20px', background: '#f0efea', color: '#333' }}>
                                ⬛ {name.split(' ').slice(-1)[0]}
                              </span>
                            ))}
                            {redCards.map(name => (
                              <span key={name} style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '20px', background: '#FCEBEB', color: '#A32D2D' }}>
                                🟥 {name.split(' ').slice(-1)[0]}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', flexShrink: 0 }}>
                        <span style={{ fontSize: '13px', fontWeight: '700', padding: '4px 10px', borderRadius: '20px', background: result.bg, color: result.color }}>{result.label}</span>
                        <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '20px', background: badge.bg, color: badge.color, fontWeight: '500' }}>{gameTypeLabel(f.game_type)}</span>
                        <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                          <button onClick={() => setEditingFixture({ ...f })} style={editBtn}>Edit Fixture</button>
                          <button onClick={() => deleteFixture(f.id)} style={deleteBtn}>Del</button>
                        </div>
                        <button onClick={() => setReportFixture(f)}
                          style={{ marginTop: '4px', fontSize: '11px', padding: '3px 8px', borderRadius: '6px', background: '#E6F1FB', color: '#185FA5', border: 'none', cursor: 'pointer', width: '100%' }}>
                          📊 Stats
                        </button>
                        {f.status === 'completed' && (
                          <button onClick={() => setAIAnalysisFixture(f)}
                            style={{ marginTop: '4px', fontSize: '11px', padding: '3px 8px', borderRadius: '6px', background: '#f0f5ff', color: '#185FA5', border: 'none', cursor: 'pointer', width: '100%' }}>
                            🧠 Analysis
                          </button>
                        )}
                        <button onClick={() => setAIReportFixture(f)}
                          style={{ marginTop: '4px', fontSize: '11px', padding: '3px 8px', borderRadius: '6px', background: '#e8f5e9', color: '#2e7d32', border: 'none', cursor: 'pointer', width: '100%' }}>
                          📰 Match Report
                        </button>
                        <button onClick={() => setTeamSheetFixture(f)}
                          style={{ marginTop: '4px', fontSize: '11px', padding: '3px 8px', borderRadius: '6px', background: '#EEEDFE', color: '#533AB7', border: 'none', cursor: 'pointer', width: '100%' }}>
                          📄 Team Sheet
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </>
      )}
      {reportFixture && (
        <MatchReportModal
          fixture={reportFixture}
          onClose={() => setReportFixture(null)}
          getClubLogo={getClubLogo}
          myClubLogo={myClubLogo}
          currentUserEmail={profile?.email}
          currentUserName={profile?.full_name}
          clubName={profile?.clubs?.name || profile?.clubs?.club_name}
        />
      )}
      {aiReportFixture && (
        <AIMatchReportModal
          fixture={aiReportFixture}
          squadName={aiReportFixture.squads?.name || ''}
          clubName={profile?.clubs?.name || ''}
          currentUserEmail={profile?.email}
          onClose={() => setAIReportFixture(null)}
        />
      )}
      {aiAnalysisFixture && (
        <AIMatchAnalysisModal
          fixture={aiAnalysisFixture}
          squadName={aiAnalysisFixture.squads?.name || ''}
          clubName={profile?.clubs?.name || ''}
          currentUserEmail={profile?.email}
          onClose={() => setAIAnalysisFixture(null)}
        />
      )}
      {teamSheetFixture && (
        <TeamSheetModal
          fixture={teamSheetFixture}
          profile={profile}
          onClose={() => setTeamSheetFixture(null)}
        />
      )}
      {showExport && (
        <FixturesExportModal
          fixtures={displayFixtures}
          squadName={squads.find(s => s.id === (filterSquad !== 'all' ? filterSquad : squads[0]?.id))?.name || 'Squad'}
          clubName={profile?.clubs?.club_name || profile?.clubs?.name || ''}
          season={filterSeason}
          profile={profile}
          onClose={() => setShowExport(false)}
        />
      )}
    </div>
  )
}

function EditFixtureForm({ fixture, squads, onChange, onSave, onCancel, clubs }) {
  const [showClubPicker, setShowClubPicker] = useState(false)
  const filtered = clubs.filter(c => c.name.toLowerCase().includes(fixture.opponent?.toLowerCase() || ''))
  return (
    <div style={{ margin: '8px 16px', padding: '14px', background: '#f8f8f8', borderRadius: '12px', border: '1px solid #e0e0e0' }}>
      <p style={{ fontSize: '13px', color: '#888', marginBottom: '10px' }}>Edit fixture</p>
      <div style={{ position: 'relative', marginBottom: '10px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', border: '1px solid #e0e0e0', borderRadius: '8px', padding: '6px 10px', background: 'white' }}>
          <img src={getClubLogo(fixture.opponent)} alt="" style={{ width: '24px', height: '24px', borderRadius: '50%', objectFit: 'contain' }} onError={e => { e.target.src = DEFAULT_LOGO }} />
          <input style={{ border: 'none', outline: 'none', fontSize: '13px', flex: 1 }}
            value={fixture.opponent || ''}
            onChange={e => { onChange({ ...fixture, opponent: e.target.value }); setShowClubPicker(true) }}
            onFocus={() => setShowClubPicker(true)} />
        </div>
        {showClubPicker && filtered.length > 0 && (
          <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, background: 'white', border: '1px solid #e0e0e0', borderRadius: '8px', maxHeight: '150px', overflowY: 'auto', zIndex: 50, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
            {filtered.slice(0, 10).map(c => (
              <div key={c.name} onClick={() => { onChange({ ...fixture, opponent: c.name }); setShowClubPicker(false) }}
                style={{ padding: '8px 12px', borderBottom: '0.5px solid #f0f0f0', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <img src={c.logo} alt="" style={{ width: '24px', height: '24px', borderRadius: '50%', objectFit: 'contain' }} onError={e => { e.target.src = DEFAULT_LOGO }} />
                {c.name}
              </div>
            ))}
          </div>
        )}
      </div>
      <select style={editInput} value={fixture.squad_id} onChange={e => onChange({ ...fixture, squad_id: e.target.value })}>
        {squads.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
      </select>
      <select style={editInput} value={fixture.team_number} onChange={e => onChange({ ...fixture, team_number: e.target.value })}>
        <option value="1">{getTeamLabel(1, squads.find(s => s.id === fixture.squad_id)?.squad_teams)}</option>
        <option value="2">{getTeamLabel(2, squads.find(s => s.id === fixture.squad_id)?.squad_teams)}</option>
      </select>
      <select style={editInput} value={fixture.sport} onChange={e => onChange({ ...fixture, sport: e.target.value })}>
        <option value="football">Gaelic Football</option>
        <option value="hurling">Hurling</option>
      </select>
      <select style={editInput} value={fixture.game_type} onChange={e => onChange({ ...fixture, game_type: e.target.value })}>
        <option value="league">League</option>
        <option value="championship">Championship</option>
        <option value="cup">Cup</option>
        <option value="friendly">Friendly</option>
      </select>
      <input style={editInput} type="datetime-local" value={fixture.fixture_date?.slice(0, 16) || ''}
        onChange={e => onChange({ ...fixture, fixture_date: e.target.value })} />
      <input style={editInput} placeholder="Venue" value={fixture.venue || ''}
        onChange={e => onChange({ ...fixture, venue: e.target.value })} />
        <input style={editInput} placeholder="Venue map link (optional)" value={fixture.venue_url || ''}
        onChange={e => onChange({ ...fixture, venue_url: e.target.value })} />
      <div style={{ display: 'flex', gap: '8px' }}>
        <button style={{ flex: 1, padding: '12px', background: '#185FA5', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }} onClick={onSave}>Save</button>
        <button style={{ flex: 1, padding: '12px', border: '0.5px solid #ccc', borderRadius: '8px', background: 'white', fontSize: '14px', cursor: 'pointer' }} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  )
}

const header = { padding: '16px', borderBottom: '0.5px solid #e0e0e0', display: 'flex', alignItems: 'center' }
const h2 = { fontSize: '17px', fontWeight: '500', flex: 1 }
const addBtn = { padding: '6px 14px', background: '#185FA5', color: 'white', border: 'none', borderRadius: '20px', fontSize: '13px', cursor: 'pointer' }
const formBox = { margin: '12px 16px', padding: '14px', background: '#f8f8f8', borderRadius: '12px' }
const formTitle = { fontSize: '13px', color: '#888', marginBottom: '10px' }
const input = { display: 'block', width: '100%', padding: '10px 12px', marginBottom: '10px', border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', background: 'white', boxSizing: 'border-box' }
const editInput = { display: 'block', width: '100%', padding: '8px 10px', marginBottom: '8px', border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '13px', background: 'white', boxSizing: 'border-box' }
const saveBtn = { display: 'block', width: '100%', padding: '12px', background: '#185FA5', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' }
const fixtureRow = { display: 'flex', alignItems: 'center', gap: '8px', padding: '12px 16px', borderBottom: '0.5px solid #f0f0f0' }
const editBtn = { fontSize: '11px', padding: '3px 8px', borderRadius: '6px', border: '0.5px solid #185FA5', background: '#E6F1FB', color: '#185FA5', cursor: 'pointer' }
const deleteBtn = { fontSize: '11px', padding: '3px 8px', borderRadius: '6px', border: '0.5px solid #A32D2D', background: '#FCEBEB', color: '#A32D2D', cursor: 'pointer' }

function PreviousResults({ fixture, allFixtures, onOpen }) {
  const [expanded, setExpanded] = useState(false)

  const prev = allFixtures.filter(f =>
    f.id !== fixture.id &&
    f.status === 'completed' &&
    f.opponent === fixture.opponent &&
    f.squad_id === fixture.squad_id &&
    f.sport === fixture.sport &&
    String(f.team_number || 1) === String(fixture.team_number || 1)
  ).sort((a, b) => new Date(b.fixture_date) - new Date(a.fixture_date)).slice(0, 5)

  if (prev.length === 0) return null

  function formatScore(f) {
    const hTotal = (f.home_goals * 3) + f.home_points
    const aTotal = (f.away_goals * 3) + f.away_points
    return f.home_goals + '-' + String(f.home_points).padStart(2, '0') +
      ' (' + hTotal + ') v ' +
      f.away_goals + '-' + String(f.away_points).padStart(2, '0') +
      ' (' + aTotal + ')'
  }

  function getResult(f) {
    const hTotal = (f.home_goals * 3) + f.home_points
    const aTotal = (f.away_goals * 3) + f.away_points
    if (hTotal > aTotal) return { label: 'W', color: '#3B6D11', bg: '#EAF3DE' }
    if (hTotal < aTotal) return { label: 'L', color: '#A32D2D', bg: '#FCEBEB' }
    return { label: 'D', color: '#854F0B', bg: '#FAEEDA' }
  }

  return (
    <div style={{ marginTop: 6 }}>
      <button
        onClick={e => { e.stopPropagation(); setExpanded(!expanded) }}
        style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: '#f0f0f0', color: '#555', border: 'none', cursor: 'pointer' }}
      >
        {expanded ? '▲ Hide' : '📊 vs ' + fixture.opponent + ' (' + prev.length + ' previous)'}
      </button>
      {expanded && (
        <div style={{ marginTop: 6, background: '#f8f8f8', borderRadius: 8, padding: '8px 10px' }}>
          <div style={{ fontSize: 10, color: '#888', fontWeight: 600, marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Previous results vs {fixture.opponent}
          </div>
          {prev.map(f => {
            const result = getResult(f)
            const d = new Date(f.fixture_date)
            const dateStr = d.toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric' })
            return (
              <div key={f.id}
                onClick={e => { e.stopPropagation(); onOpen && onOpen(f) }}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '0.5px solid #f0f0f0', cursor: onOpen ? 'pointer' : 'default' }}>
                <span style={{ fontSize: 11, color: '#aaa', minWidth: 80 }}>{dateStr}</span>
                <span style={{ fontSize: 12, flex: 1, color: '#333' }}>{formatScore(f)}</span>
                <span style={{ fontSize: 11, fontWeight: 700, padding: '1px 6px', borderRadius: 10, background: result.bg, color: result.color }}>
                  {result.label}
                </span>
                {onOpen && <span style={{ fontSize: 11, color: '#aaa' }}>→</span>}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}